import React, { useState } from "react";
import { Typography, Chip, Avatar } from "@mui/material";
import CustomTable from "../../../components/Table/Table";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useBusinessUnitsViewModel } from "./BusinessUnitViewModel";
import AddBusinessUnit from "../AddBusinessUnit/AddBusinessUnit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { StyledCardContent } from "./BusinessUnitStyles";
import { useLoading } from "../../../contexts/LoadingContext";
import Modal from "../../../components/Modal/Modal";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import BusinessIcon from "@mui/icons-material/Business";
import CircleIcon from "@mui/icons-material/Circle";
import FactoryIcon from "@mui/icons-material/Factory";
import PublicIcon from "@mui/icons-material/Public";
import { useRbac } from "../../../hooks/RbacContext";

const BusinessUnits = () => {
  const {
    businessUnits,
    loading,
    error,
    handleDelete,
    createBusinessUnit,
    updateBusinessUnit,
    refetch,
    handleSearch,
  } = useBusinessUnitsViewModel();

  const { showLoading, hideLoading } = useLoading();

  const [isAddBusinessUnitOpen, setIsAddBusinessUnitOpen] = useState(false);
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const { checkAccess } = useRbac();

  const columns = [
    {
      id: "business_unit_name",
      label: "Name",
    },
    {
      id: "created_on",
      label: "Created On",
      align: "left",
      render: (value) => {
        if (!value) return '-';
        const [day, month, year] = value.split('/');
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        });
      }
    },
    {
      id: "updated_on",
      label: "Updated On",
      align: "left",
      render: (value) => {
        if (!value) return '-';
        const [day, month, year] = value.split('/');
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        });
      }
    },
  ];
  const fixedColumns = {
    left: [],
    right: ["actions"],
  };
  const handleAddBusinessUnit = () => {
    setSelectedBusinessUnit(null);
    setIsAddBusinessUnitOpen(true);
  };

  const handleEditBusinessUnit = (businessUnit) => {
    setSelectedBusinessUnit(businessUnit);
    setIsAddBusinessUnitOpen(true);
  };

  const handleCloseDialog = () => {
    setIsAddBusinessUnitOpen(false);
    setSelectedBusinessUnit(null);
  };

  const handleSubmit = async (formData) => {
    try {
      showLoading();
      if (selectedBusinessUnit) {
        await updateBusinessUnit(
          selectedBusinessUnit.business_unit_id,
          formData
        );
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Business Unit updated successfully",
        });
      } else {
        await createBusinessUnit(formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Business Unit added successfully",
        });
      }
      handleCloseDialog();
    } catch (error) {
      throw error;
    } finally {
      hideLoading();
    }
  };

  const handleCloseSuccessModal = () => {
    setSuccessModal(null);
  };

  const handleDeleteClick = (businessUnit) => {
    setDeleteConfirmation({
      businessUnit,
      open: true,
    });
  };

  const handleDeleteConfirm = async () => {
    const businessUnit = deleteConfirmation.businessUnit;
    try {
      showLoading();
      await handleDelete(businessUnit);
      setDeleteConfirmation(null);
      setSuccessModal({
        type: "success",
        title: "Success",
        message: "Business Unit deleted successfully",
      });
    } catch (error) {
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: error.message || "Failed to delete business unit",
      });
    } finally {
      hideLoading();
    }
  };

  const getActions = (businessUnit) => {
    const actions = [];
    if (checkAccess("businessUnit", "update")) {
      actions.push({
        icon: <EditIcon fontSize="small" />,
        label: "Edit",
        onClick: handleEditBusinessUnit,
        color: "primary",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.primary.lighter || "rgba(33, 150, 243, 0.08)",
          },
        },
      });
    }
    if (checkAccess("businessUnit", "delete")) {
      actions.push({
        icon: <DeleteIcon fontSize="small" />,
        label: "Delete",
        onClick: handleDeleteClick,
        color: "error",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.error.lighter || "rgba(211, 47, 47, 0.08)",
          },
        },
      });
    }

    return actions;
  };

  if (error) {
    return (
      <StyledCardContent>
        <Typography color="error">Error: {error}</Typography>
      </StyledCardContent>
    );
  }
  const columnFilters = [
    {
      id: "business_unit_name",
      type: "select",
      label: "Business Unit Name",
      icon: <BusinessIcon />,
      sendIcon: true,
    },
  ];

  return (
    <div>
      <CustomTable
        columns={columns}
        data={businessUnits}
        onAdd={handleAddBusinessUnit}
        showActions={true}
        showAddButton={checkAccess("businessUnit", "add")}
        addButtonIcon={<PersonAddIcon />}
        showSearch={true}
        showColumnVisibility={false}
        pageSize={10}
        stickyHeader={true}
        fixedColumns={fixedColumns}
        actions={getActions}
        title="Business Units"
        subtitle="Manage and monitor all business units"
        addButtonText="Add Business Unit"
        alignment="left"
        fontSize="12px"
        showFilters={true}
        columnFilters={columnFilters}
      />

      <AddBusinessUnit
        open={isAddBusinessUnitOpen}
        onClose={handleCloseDialog}
        initialData={selectedBusinessUnit}
        onSubmit={handleSubmit}
      />

      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={handleCloseSuccessModal}
        />
      )}

      {deleteConfirmation && (
        <ConfirmationModal
          open={true}
          title="Delete Business Unit"
          message={`Are you sure you want to delete business unit "${deleteConfirmation.businessUnit.business_unit_name}"?`}
          onClose={() => setDeleteConfirmation(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default BusinessUnits;
